import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Globe, Languages } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PageHero } from "@/components/Sections";
import { FaqAccordion } from "@/components/FaqAccordion";
import { QuoteForm } from "@/components/forms/QuoteForm";
import { ContactForm } from "@/components/forms/ContactForm";
import { ComplaintForm } from "@/components/forms/ComplaintForm";
import { CONTACT } from "@/components/Footer";

const FORMS = { quote: QuoteForm, simple: ContactForm, complaint: ComplaintForm };

export default function ContactPage() {
  const { t } = useLang();
  const c = t.contact;
  const [tab, setTab] = useState("quote");
  const [cat, setCat] = useState(0);
  const Form = FORMS[tab];

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow={c.hero.eyebrow} title={c.hero.title} sub={c.hero.sub} id="contact" />

      <section className="container-x pb-20 lg:pb-28" id="contact-forms" data-testid="contact-forms-section">
        <div className="grid gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 sm:p-10">
              <div className="mb-8 flex flex-wrap gap-2 rounded-full bg-slate-100 p-1.5" role="tablist">
                {Object.keys(FORMS).map((k) => (
                  <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`relative flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "text-white" : "text-slate-600 hover:text-slate-900"}`} data-testid={`contact-tab-${k}`}>
                    {tab === k && <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-slate-900" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
                    <span className="relative">{c.tabs[k]}</span>
                  </button>
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
                  <Form />
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-4" delay={0.1}>
            <div className="grain relative h-full overflow-hidden rounded-[2rem] bg-navy p-8 text-white">
              <div className="relative">
                <p className="eyebrow text-teal-300">{c.info.title}</p>
                <ul className="mt-8 space-y-6 text-sm">
                  <li className="flex gap-4"><Phone size={18} className="mt-0.5 shrink-0 text-teal-300" /><div><p className="text-slate-400">{c.info.phone}</p><a href={CONTACT.phoneHref} className="font-display text-lg font-semibold hover:text-teal-200" data-testid="contact-phone-link">{CONTACT.phone}</a></div></li>
                  <li className="flex gap-4"><Mail size={18} className="mt-0.5 shrink-0 text-teal-300" /><div><p className="text-slate-400">{c.info.email}</p><a href={`mailto:${CONTACT.email}`} className="font-display text-lg font-semibold hover:text-teal-200" data-testid="contact-email-link">{CONTACT.email}</a></div></li>
                  <li className="flex gap-4"><MapPin size={18} className="mt-0.5 shrink-0 text-teal-300" /><div><p className="text-slate-400">{c.info.address}</p><p className="font-medium" data-testid="contact-address">{CONTACT.address}</p></div></li>
                  <li className="flex gap-4"><Clock size={18} className="mt-0.5 shrink-0 text-teal-300" /><div><p className="text-slate-400">{c.info.hours}</p><p className="font-medium">{c.info.hoursValue}</p></div></li>
                  <li className="flex gap-4"><Globe size={18} className="mt-0.5 shrink-0 text-teal-300" /><div><p className="text-slate-400">{c.info.zone}</p><p className="font-medium">{c.info.zoneValue}</p></div></li>
                  <li className="flex gap-4"><Languages size={18} className="mt-0.5 shrink-0 text-teal-300" /><div><p className="text-slate-400">{c.info.languages}</p><p className="font-medium">{c.info.languagesValue}</p></div></li>
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" id="faq" data-testid="contact-faq-section">
        <div className="container-x">
          <SectionHeading eyebrow={c.faq.eyebrow} title={c.faq.title} id="contact-faq" />
          <div className="mt-12 grid gap-10 lg:grid-cols-12">
            <div className="flex flex-wrap gap-2 lg:col-span-3 lg:flex-col">
              {c.faq.categories.map((cg, i) => (
                <button key={cg.name} type="button" onClick={() => setCat(i)} className={`rounded-full border px-4 py-2.5 text-left text-sm font-medium transition-colors lg:rounded-2xl ${cat === i ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-teal-400"}`} data-testid={`faq-category-${i}`}>{cg.name}</button>
              ))}
            </div>
            <div className="lg:col-span-9">
              <AnimatePresence mode="wait">
                <motion.div key={cat} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
                  <FaqAccordion items={c.faq.categories[cat].items} idPrefix={`faq-${cat}`} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
